// A faithful visual replica of the v2 application form
// (v2.commissaryconnect.com/forms/application-form/), rebuilt from the live
// page: six MUI vertical-stepper steps, the same questions in the same order,
// the same field types and the same copy.
//
// LOCAL ONLY — this is not wired to /api/apply and must not be deployed. It
// exists so ours can be compared side by side with theirs.
//
// ONE DELIBERATE DIFFERENCE: v2 lists the Kitsilano facility as "Lazy
// Gourmet". That name is retired and must never appear on this site
// (CLAUDE.md), so it reads "Commissary Connect Kitsilano" here. Same facility,
// same position in the list.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import LIMITS from '../lib/fieldLimits';
import { postApplication, primeFormToken } from '../lib/applyClient';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { V2_FACILITIES, V2_OFFSITE_FORMS, kitchens as KITCHEN_DATA } from '../data/kitchens';
import { FACILITIES } from '../data/facilityPages';
import { AW_APPLICATION_CONVERSION, applicationAttribution } from '../lib/attribution';
import { posthog, identifyApplicant, deviceId } from '../lib/posthog';
import { V2_REPLICA_DRAFT_KEY, DRAFT_TTL_MS } from '../lib/storageKeys';
import './V2FormReplica.css';

const DRAFT_KEY = V2_REPLICA_DRAFT_KEY;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// v2's facilities and its two off-site siblings come from the one mapping in
// data/kitchens.ts (CMSY-28). OFFSITE is filtered to what v2's own form lists,
// in v2's order, so the replica still matches v2 exactly.
const KITCHENS = V2_FACILITIES;
// Each kitchen's street address, shown under its name (CMSY-91, Jeffrey
// 2026-09-16) so nobody has to leave the form to learn where a kitchen is.
// The join is V2_FACILITIES.kitchenId → kitchens.ts, with facilityPages.ts as
// a fallback for a kitchen that has a landing page but no dataset entry. No
// address → name only, never "undefined". (Richmond was the one kitchen that
// needed the fallback; it was removed on 2026-09-16, CMSY-96. The fallback
// stays — it is the reason a missing entry degrades instead of printing
// "undefined".)
const addressFor = (kitchenId: string): string | undefined =>
  KITCHEN_DATA.find((k) => k.id === kitchenId)?.address
  ?? Object.values(FACILITIES).find((p) => p.kitchenId === kitchenId)?.address;
const OFFSITE = V2_OFFSITE_FORMS.filter((f) => f.v2FormHref).map((f) => ({ name: f.name, href: f.v2FormHref as string }));
// v2's own list. The form renders it alphabetically; the ids are v2's, read
// from GET https://v2.commissaryconnect.com/api/memberships/types on
// production 2026-09-01 — that endpoint, not our bundle, is the source of
// truth. Note BOTH "Food Cart" (40) and "Food Cart/Truck" (6) exist.
// The superseded ApplicationForm.tsx hardcoded nine of these and was missing
// id 40, so a food cart could not pick its own business type; it was deleted at
// the 2026-09-02 relaunch and this list is now the only one.
const BUSINESS_TYPES = [
  { id: 7, name: 'Caterer' },
  { id: 40, name: 'Food Cart' },
  { id: 6, name: 'Food Cart/Truck' },
  { id: 37, name: 'Food Delivery' },
  { id: 38, name: 'Food/Beverage Production' },
  { id: 9, name: 'Meal Prep' },
  { id: 10, name: 'Other' },
  { id: 8, name: 'Processing' },
  { id: 39, name: 'Restaurant' },
  { id: 49, name: 'Takeout Restaurant' },
];
const EQUIPMENT = ['Oven', 'Range', 'Low Burner', 'Fryer', 'Mixer', 'Dough Sheeter', 'Cooler Space'];
const DAYS = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];
const HEARD = ['Google Search', 'Social Media', 'Friend/Family', 'Business Partner', 'Event/Trade Show', 'Other'];
const STEPS = ['Kitchens', 'Contact Info', 'Company Info', 'Kitchen Info', 'Social Media', 'Final Review'];

// CMSY-98. A refused step used to be a purely visual event: red text appeared,
// nothing was announced, and focus stayed on the button — so a screen-reader
// user pressed Next and perceived nothing at all. The live region below names
// what is still needed, in the order the fields appear; each field also points
// at its own message through aria-describedby, and focus moves to the first one.
const DAYS_ERROR_ID = 'v2f-days-error';
const ERROR_ORDER = ['firstName', 'lastName', 'email', 'phone', 'typeId', 'productType', 'moveIn', 'days'] as const;
const ERROR_FIELD_NAMES: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email Address',
  phone: 'Phone Number',
  typeId: 'Type of Business',
  productType: 'What dishes or menu items you make',
  moveIn: 'Desired move in date',
  days: 'The days and times you are available',
};
/** "First Name, Last Name and Email Address" — read out, so it is a sentence. */
const listNames = (keys: string[]) => {
  const names = keys.map((k) => ERROR_FIELD_NAMES[k] ?? k);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

// 30-minute increments, 12:00 AM → 11:30 PM, exactly as v2 builds them.
// Every <option> built from this list carries an explicit `value` (CMSY-122). Without one
// the browser uses the option's TEXT as its value, and a visitor reading the site through
// Google Translate had that text translated: "9:00 AM" became Chinese, `TIMES.indexOf`
// could not find it, and the schedule step refused valid hours. Found by running the real
// translator against the form; pinned by e2e/translation.spec.ts.
const TIMES = Array.from({ length: 48 }, (_, i) => {
  const h24 = Math.floor(i / 2);
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${i % 2 ? '30' : '00'} ${h24 < 12 ? 'AM' : 'PM'}`;
});

// Move-in bounds for the native date picker. A free-text MM/DD/YYYY field let a
// tester submit the year 0206, which we forwarded to v2 without complaint.
// Calendar dates in the applicant's OWN day. Two traps, both live until
// 2026-09-16 (CMSY-88): toISOString() is the UTC date, which is tomorrow in
// Vancouver after 5pm, so the picker's min= refused today every evening; and
// new Date('YYYY-MM-DD') parses as UTC MIDNIGHT — 5pm the previous day here —
// so a move-in of today compared below local midnight and was rejected as
// "in the past" at every hour, for every applicant. Local getters and a
// local-midnight constructor, never the ISO/UTC forms, for a date with no time.
const pad2 = (n: number) => String(n).padStart(2, '0');
const isoDay = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseDay = (v: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(NaN);
};
const todayISO = isoDay(new Date());
const maxMoveInISO = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 5);
  return isoDay(d);
})();
// A date input yields YYYY-MM-DD; the review step should still read MM/DD/YYYY.
const displayDate = (v: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : v;
};

// MUI v4 draws its own checkbox and radio as 24px SVGs — a native control is
// visibly chunkier and sits differently, which is the single most obvious
// tell when the two forms are held side by side. These are MUI's own paths.
const BOX_OUTLINE = 'M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z';
const BOX_CHECKED =
  'M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z';
const RADIO_OUTLINE =
  'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z';
const RADIO_CHECKED = RADIO_OUTLINE + ' M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z';

const Check: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
  type?: 'checkbox' | 'radio';
  name?: string;
  /** A second, quieter line under the label — the kitchen's address (CMSY-91).
   *  It sits inside the <label>, so tapping it toggles the box; the input's
   *  accessible name stays the label alone and the hint is its description. */
  hint?: string;
  /** CMSY-98: the days group is the one error that belongs to a set of boxes
   *  rather than to a Field, so each box carries the invalid state and points
   *  at the one message. */
  invalid?: boolean;
  describedBy?: string;
}> = ({ label, checked, onChange, type = 'checkbox', name, hint, invalid, describedBy }) => {
  const id = React.useId();
  return (
    <label className={hint ? 'v2form-check has-hint' : 'v2form-check'}>
      <span className="v2form-box">
        <input
          type={type}
          name={name}
          checked={checked}
          onChange={onChange}
          aria-labelledby={`${id}-l`}
          aria-describedby={[hint ? `${id}-h` : '', describedBy || ''].filter(Boolean).join(' ') || undefined}
          aria-invalid={invalid ? true : undefined}
        />
        <svg viewBox="0 0 24 24" className={checked ? 'v2form-svg is-on' : 'v2form-svg'} aria-hidden>
          <path d={type === 'radio' ? (checked ? RADIO_CHECKED : RADIO_OUTLINE) : checked ? BOX_CHECKED : BOX_OUTLINE} />
        </svg>
      </span>
      {hint ? (
        <span className="v2form-checktext">
          <span id={`${id}-l`} className="v2form-checkname">{label}</span>
          <span id={`${id}-h`} className="v2form-checkhint">{hint}</span>
        </span>
      ) : (
        <span id={`${id}-l`} className="v2form-checkname">{label}</span>
      )}
    </label>
  );
};

// v2 omits the value line entirely when there is no answer — the row keeps its
// height only because the other cell in that grid row has one.
const Pair: React.FC<{ label: string; value: string; full?: boolean }> = ({ label, value, full }) => (
  <div className={full ? 'v2form-pair is-full' : 'v2form-pair'}>
    <div className="v2form-pair-label">{label}</div>
    {value ? <div className="v2form-pair-value">{value}</div> : null}
  </div>
);

// MUI renders the floating label as a real <label> bound to its input; ours was
// a bare <span>, which left every field unlabelled for screen readers and for
// tests. The id is derived from the label text.
const fieldId = (label: string) =>
  'v2f-' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Field: React.FC<{
  label: string;
  error?: string;
  adorn?: React.ReactNode;
  /** v2 renders the date field as MUI's OUTLINED variant: white box, 1px
   *  border, and the label sitting above it rather than floating inside. */
  outlined?: boolean;
  children: React.ReactElement;
}> = ({ label, error, adorn, outlined, children }) => {
  const id = fieldId(label);
  // CMSY-98: the message needs an id so the control can point at it. Without
  // this the red text was visible and nothing else — a screen reader was told
  // nothing at all when a step was refused.
  const errorId = `${id}-error`;
  const [focused, setFocused] = useState(false);
  // MUI shrinks the label only once the field has a value or the caret is in
  // it; ours was permanently shrunk, so every empty field read differently.
  const value = (children.props as { value?: unknown }).value;
  const filled = String(value ?? '') !== '';
  const cls = [
    'v2form-field',
    outlined ? 'is-outlined' : '',
    error ? 'is-error' : '',
    filled || focused || outlined ? 'is-float' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <label className="v2form-fieldlabel" htmlFor={id}>{label}</label>
      {React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : undefined,
        onFocus: () => setFocused(true),
        onBlur: (e: React.FocusEvent) => {
          setFocused(false);
          const childBlur = (children.props as { onBlur?: (e: React.FocusEvent) => void }).onBlur;
          if (childBlur) childBlur(e);
        },
      })}
      {adorn}
      {error && <p className="v2form-error" id={errorId}>{error}</p>}
    </div>
  );
};

const V2FormReplica: React.FC = () => {
  // v2's form is a bare page — no site header, no footer. Hide this site's
  // chrome while the replica is mounted so the two can be compared honestly.
  useEffect(() => {
    document.body.classList.add('v2form-bare');
    // Mint the form token now, while the applicant is still reading, so the
    // submit itself never waits on a round trip (CMSY-108). Best effort: if it
    // fails the submit goes without one and the server stores the row anyway.
    primeFormToken();
    return () => document.body.classList.remove('v2form-bare');
  }, []);

  // Draft persistence, same contract as ApplicationForm: leaving and coming
  // back must never mean starting over. 7-day TTL, best effort.
  const draft = useMemo(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (!d?.at || Date.now() - d.at > DRAFT_TTL_MS) return null;
      return d;
    } catch { return null; }
  }, []);

  const [searchParams] = useSearchParams();
  // One key per form session: the partial row upgrades to submitted in place.
  // No key at all is better than a fake one — the API accepts null and inserts.
  const [clientKey] = useState(() => draft?.clientKey || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : null));
  const [openedAt] = useState(() => draft?.openedAt ?? Date.now());
  const [website, setWebsite] = useState(''); // honeypot — hidden from humans
  // A ref, not state: one click dispatches click AND change in the same React
  // batch, and state still reads its old value in both handlers — which fired
  // application_form_started twice. Nothing renders off this, so a ref is both
  // correct and sufficient.
  const startedRef = useRef(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [doneId, setDoneId] = useState<number | null>(null);

  // UX timing and friction counters. PostHog-only by design: Supabase does not
  // need them, PostHog cannot derive them without timestamp arithmetic.
  const stepEnteredAt = useRef(Date.now());
  const backCount = useRef(0);
  const blockedCount = useRef(0);
  const furthestStep = useRef(0);
  const leftSent = useRef(false);
  const visitedAt = useRef(Date.now()); // this page load, unlike openedAt (restored from a draft)
  const secs = (since: number) => Math.round((Date.now() - since) / 1000);

  const [step, setStep] = useState(0);
  const [kitchens, setKitchens] = useState<number[]>(draft?.kitchens ?? []);
  const [contact, setContact] = useState(draft?.contact ?? { firstName: '', lastName: '', email: '', phone: '' });
  const [company, setCompany] = useState(draft?.company ?? { company: '', typeId: '', productType: '', moveIn: '' });
  const [equipment, setEquipment] = useState<string[]>(draft?.equipment ?? []);
  const [otherAppliance, setOtherAppliance] = useState(draft?.otherAppliance ?? '');
  const [days, setDays] = useState<Record<string, { on: boolean; start: string; end: string }>>(
    draft?.days ?? Object.fromEntries(DAYS.map((d) => [d, { on: false, start: '', end: '' }])),
  );
  const [otherKitchen, setOtherKitchen] = useState(draft?.otherKitchen ?? '');
  const [social, setSocial] = useState(draft?.social ?? { instagram: '', twitter: '', linkedIn: '', otherSocials: '' });
  const [heard, setHeard] = useState<string>(draft?.heard ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // CMSY-98 focus management. A step change is a navigation for everyone else;
  // without moving focus a keyboard or screen-reader user is left wherever the
  // last click put them, with the new step unannounced.
  const contentRef = useRef<HTMLDivElement | null>(null);
  const focusFirstInvalid = useRef(false);
  const doneRef = useRef<HTMLDivElement | null>(null);
  const firstRender = useRef(true);

  // ── instrumentation (the whole point of owning the form) ────────────────
  // Every funnel event carries the same context: form_variant (the main form
  // emits identical event names, so without it the two are indistinguishable),
  // client_key (joins the PostHog journey to the Supabase row), resumed, and
  // seconds_since_open so time-to-anything is a property, not timestamp math.
  const base = () => ({
    form_variant: 'v2_replica',
    client_key: clientKey,
    resumed: !!draft,
    seconds_since_open: secs(openedAt),
    seconds_since_visit: secs(visitedAt.current), // CMSY-40
    step: STEPS[step],
    step_index: step + 1,
  });
  const track = (event: string, props?: Record<string, unknown>, opts?: { transport: 'sendBeacon' }) => {
    try { if (posthog.__loaded) posthog.capture(event, { ...base(), ...props }, opts); } catch { /* degrade silently */ }
  };
  // `onFocusCapture` alone undercounts. Safari and Chrome iOS do not move focus
  // to a checkbox when it is clicked, so a visitor who ticked a kitchen and left
  // never counted as having started. Measured 2026-09-07 over the week: Chrome
  // 4 past the kitchens step / 4 starts, but Mobile Safari 5/2 and Chrome iOS
  // 3/0 — the metric was blind on the browsers most of the traffic uses.
  // markStarted is now also called on any interaction, not only focus.
  const markStarted = () => {
    // The guard must be a ref, not state. One click dispatches click AND change
    // in the same React batch; `started` still reads false in both handlers, so
    // a state guard fires the event twice. Caught by the test below.
    if (!startedRef.current) {
      startedRef.current = true;
      track('application_form_started', {
        preselected: searchParams.has('facilities'),
        // Which kitchens were already ticked at the moment of the first
        // interaction — tells us what they came for, even if they leave.
        facility_ids_at_start: kitchens,
      });
      // GA4 gets the start too, so start→submit is a rate GA4 can show on its own.
      window.gtag?.('event', 'application_form_started', { event_category: 'Application' });
    }
  };

  // Per-step timing: reset the clock and note how far the visitor has got.
  useEffect(() => {
    stepEnteredAt.current = Date.now();
    if (step > furthestStep.current) furthestStep.current = step;
  }, [step]);

  // Set by next() when a step is refused; runs here, after the DOM carries the
  // aria-invalid the query looks for.
  useEffect(() => {
    if (!focusFirstInvalid.current) return;
    focusFirstInvalid.current = false;
    document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [errors]);

  // Passing a step is a navigation: move focus into the step that just opened
  // so it is announced and the next Tab starts there. Not on first paint —
  // that would yank the page on arrival.
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    contentRef.current?.focus();
  }, [step]);

  // The confirmation replaces the whole form; focus it or the announcement is
  // never made and a keyboard user is left on a button that no longer exists.
  useEffect(() => {
    if (doneId !== null) doneRef.current?.focus();
  }, [doneId]);

  // A restored draft is a returning visitor — the strongest "almost applied"
  // signal there is. Fired once at mount, whether or not they touch anything.
  useEffect(() => {
    if (!draft) return;
    track('application_form_resumed', {
      draft_age_seconds: Math.round((Date.now() - draft.at) / 1000),
      had_email: !!draft.contact?.email,
      kitchens_count: draft.kitchens?.length ?? 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving before completion. pagehide, not visibilitychange — a tab switch on
  // mobile is not leaving — and sendBeacon so the event survives the unload.
  // Re-bound every render so it always reads the current step and state.
  useEffect(() => {
    const onLeave = () => {
      if (leftSent.current || doneId !== null) return;
      leftSent.current = true;
      track('application_form_left', {
        last_step: STEPS[step],
        last_step_index: step + 1,
        furthest_step_index: furthestStep.current + 1,
        back_count: backCount.current,
        blocked_count: blockedCount.current,
        had_email: EMAIL_RE.test(contact.email),
        // Someone who quits on the Kitchens step leaves no row — a partial
        // needs a valid email and they have not given one. These properties are
        // the only record of what they were after, so demand for a kitchen is
        // measurable even from the people we cannot contact.
        facility_ids: kitchens,
        kitchens_count: kitchens.length,
      }, { transport: 'sendBeacon' });
    };
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  });

  // Persist the draft on every meaningful change; clear once submitted.
  useEffect(() => {
    try {
      if (doneId !== null) { localStorage.removeItem(DRAFT_KEY); return; }
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        at: Date.now(), openedAt, clientKey, kitchens, contact, company,
        equipment, otherAppliance, days, otherKitchen, social, heard,
      }));
    } catch { /* storage unavailable — degrade silently */ }
  }, [openedAt, clientKey, kitchens, contact, company, equipment, otherAppliance, days, otherKitchen, social, heard, doneId]);

  // ?facilities=3 from the kitchen cards preselects that facility (v2's ids)
  // AND opens the form on Contact Info (CMSY-92, Jeffrey 2026-09-16: "if a
  // kitchen is selected, you go right to step 2"). The visitor has already
  // chosen on the kitchen page; asking again is the step 87% quit on. Back
  // still returns to the list with their kitchen ticked. The Kitchens step
  // event is emitted so the funnel counts the step as passed, marked as such.
  useEffect(() => {
    const pre = (searchParams.get('facilities') ?? '').split(',').map(Number).filter((n) => KITCHENS.some((k) => k.id === n));
    if (!pre.length) return;
    setKitchens(pre);
    setStep(1);
    track('application_form_step', {
      step: STEPS[0], step_index: 1, seconds_on_step: 0, back_count: 0, blocked_count: 0,
      facility_ids: pre, kitchens_count: pre.length, kitchens_skipped: false, auto_advanced: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Partial capture: 81% of apply-clickers used to vanish without a trace.
  // Fire-and-forget by design — an applicant must never see this fail.
  const earlyPartialSent = useRef(false);
  const postPartial = (trigger: 'email' | 'contact_step' | 'blocked') =>
    postApplication({
        partial: true, clientKey, startedAt: openedAt, website,
        attribution: applicationAttribution(), posthogDeviceId: deviceId(), ...contact, facilityIds: kitchens,
    })
      .then((r) => track(r.ok ? 'application_form_partial_saved' : 'application_form_partial_failed', { trigger, http_status: r.status }))
      .catch((err) => track('application_form_partial_failed', { trigger, message: err?.message ?? 'unreachable' }));
  // The earliest followable moment: the email field alone becoming valid.
  const saveEarlyPartial = () => {
    if (earlyPartialSent.current || !EMAIL_RE.test(contact.email)) return;
    earlyPartialSent.current = true;
    postPartial('email');
    identifyApplicant(contact.email, {});
  };
  // Fire the moment the email becomes valid, not on blur. iOS can go straight
  // from the field to Next with no blur — the 2026-09-03 applicant did, and we
  // captured nothing (CMSY-39). saveEarlyPartial guards against firing twice.
  useEffect(() => {
    if (EMAIL_RE.test(contact.email)) saveEarlyPartial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.email]);

  // An error that outlives its cause is worse than no error: it keeps
  // aria-invalid true on a field the applicant has already corrected, and it
  // leaves red text on screen (captured on 2026-09-16 with Mondays ticked and
  // "The days you are available are required" still showing). setErrors used to
  // run only on Next/Back, so nothing ever cleared until the next press.
  useEffect(() => {
    setErrors((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      if (contact.firstName) delete next.firstName;
      if (contact.lastName) delete next.lastName;
      if (EMAIL_RE.test(contact.email)) delete next.email;
      if (!contact.phone) delete next.phone;
      else {
        const digits = contact.phone.replace(/\D/g, '');
        if (digits.length >= 7 && digits.length <= 15 && !/^(\d)\1+$/.test(digits)) delete next.phone;
      }
      if (company.typeId) delete next.typeId;
      if (company.productType) delete next.productType;
      if (company.moveIn) delete next.moveIn;
      // `days` carries two different messages. Only the "pick a day" one is
      // cleared by ticking a day; an inverted time range is re-checked on Next.
      if (prev.days === 'The days you are available are required' && DAYS.some((d) => days[d].on)) delete next.days;
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [contact, company, days]);

  const totalHours = useMemo(
    () =>
      DAYS.reduce((sum, d) => {
        const v = days[d];
        if (!v.on || !v.start || !v.end) return sum;
        const idx = (t: string) => TIMES.indexOf(t);
        return sum + Math.max(0, (idx(v.end) - idx(v.start)) / 2);
      }, 0),
    [days],
  );

  const toggle = (list: string[], set: (v: string[]) => void, value: string) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  const toggleId = (list: number[], set: (v: number[]) => void, value: number) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const next = () => {
    const e: Record<string, string> = {};
    // The Kitchens step has no rule. "Please select at least one kitchen" was
    // one of the three step-one blockers on CMSY-78 (55 of 68 abandonments on
    // this step); since 2026-09-16 an empty list is a choice (CMSY-92).
    if (step === 1) {
      if (!contact.firstName) e.firstName = 'Please enter your first name';
      if (!contact.lastName) e.lastName = 'Please enter your last name';
      // Presence was the only check here, so "sdfdsf" walked past this step.
      // v2 blocks it ("email must be a valid email"), and EMAIL_RE is the same
      // rule v2 enforces — a@b is rejected, a@b.c accepted, measured 2026-09-04.
      // Getting it wrong is expensive twice over: /api/apply rejects the whole
      // application at the final step with no field named, and the early-partial
      // save is gated on EMAIL_RE, so a typo means we capture nothing at all.
      if (!contact.email) e.email = 'Please enter your email address';
      else if (!EMAIL_RE.test(contact.email)) e.email = 'Please enter a valid email address';
      // Phone is optional: v2's parseApplicationJson requires only name, email
      // and company, so an empty phone cannot break the relay — and requiring it
      // stopped a real applicant on the Contact step (CMSY-60). CMSY-39's format
      // rule still applies to a number the applicant does type.
      if (contact.phone) {
        // 7–15 digits is the range of real numbers anywhere (E.164 caps at 15).
        // The 2026-09-02 rule demanded exactly 10 North American digits and
        // stopped the only engaged applicant of the relaunch's first day at the
        // last field (CMSY-39). A number we tidy on the call beats one we never got.
        const digits = contact.phone.replace(/\D/g, '');
        if (digits.length < 7 || digits.length > 15) e.phone = 'Please enter your phone number with area code';
        else if (/^(\d)\1+$/.test(digits)) e.phone = 'Please enter a real phone number';
      }
    }
    if (step === 2) {
      // Company name is OPTIONAL (CMSY-94, Jeffrey 2026-09-16): it blocked 7
      // people in 16 days, most of them pre-launch with no company yet. v2's
      // parseApplicationJson still requires one, so api/apply.js forwards
      // "Not provided" when this is empty; our own row keeps it null.
      if (!company.typeId) e.typeId = 'Please select your type of business';
      // v2 requires this and we did not, so an application could reach v2 through
      // us with a blank product type but never through v2's own form. Measured
      // against the live v2 form 2026-09-04: "Please enter the type of product
      // your company makes".
      if (!company.productType) e.productType = 'Please enter the type of product your company makes';
      if (!company.moveIn) e.moveIn = 'Please select your start date';
      else {
        // The field is free text (v2 uses a text input, not a date picker), so
        // "12/02/0206" parses happily to the year 206. Ramesh's test submission
        // forwarded exactly that to v2 with nothing to stop it.
        const d = parseDay(company.moveIn);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxDate = new Date(today);
        maxDate.setFullYear(today.getFullYear() + 5);
        if (Number.isNaN(d.getTime())) e.moveIn = 'Please enter a valid date';
        else if (d < today) e.moveIn = 'Please enter a start date that is not in the past';
        else if (d > maxDate) e.moveIn = 'Please enter a start date within the next five years';
      }
    }
    if (step === 3) {
      if (!DAYS.some((d) => days[d].on)) e.days = 'The days you are available are required';
      else {
        // An end at or before the start silently produced totalHours 0 — a
        // meaningless application that still reached v2's queue.
        const inverted = DAYS.filter((d) => {
          const v = days[d];
          if (!v.on || !v.start || !v.end) return false;
          return TIMES.indexOf(v.end) <= TIMES.indexOf(v.start);
        });
        if (inverted.length) {
          e.days = `End time must be after start time (${inverted.join(', ')})`;
        }
      }
    }
    setErrors(e);
    if (Object.keys(e).length) {
      // The "what stops people" signal: which step, which field, which rule,
      // and how long they fought it. `field` is a stable key for breakdowns;
      // `reason` is the human wording, which changes.
      blockedCount.current += 1;
      track('application_form_blocked', {
        step: STEPS[step], step_index: step + 1,
        field: Object.keys(e)[0], fields: Object.keys(e), reason: Object.values(e)[0],
        seconds_on_step: secs(stepEnteredAt.current),
        blocked_count: blockedCount.current,
      });
      // A block on the Contact step is the strongest "about to leave" signal we
      // have. If the email is already valid, capture the lead now (CMSY-39).
      if (step === 1 && EMAIL_RE.test(contact.email)) postPartial('blocked');
      // Take the applicant TO the first thing that needs fixing. Its own
      // message is its aria-describedby, so it is read out on arrival. The
      // move happens in an effect keyed on `errors`, never on a timer or a
      // frame: rAF can run before React has committed aria-invalid, and then
      // the query finds nothing and focus silently stays put (caught by the
      // gate on chromium, 2026-09-16).
      focusFirstInvalid.current = true;
      return;
    }
    track('application_form_step', {
      step: STEPS[step], step_index: step + 1,
      seconds_on_step: secs(stepEnteredAt.current),
      back_count: backCount.current, blocked_count: blockedCount.current,
      ...stepFacts(),
    });
    // Contact step done: save a partial NOW. An abandonment from here on is
    // a lead we can still call.
    if (step === 1) postPartial('contact_step');
    setStep((s) => s + 1);
  };
  const back = () => {
    // Going backwards is friction the funnel would otherwise never show.
    backCount.current += 1;
    track('application_form_back', {
      from_step: STEPS[step], from_step_index: step + 1, to_step_index: step,
      seconds_on_step: secs(stepEnteredAt.current), back_count: backCount.current,
    });
    setErrors({});
    setStep((s) => s - 1);
  };

  // What was decided on the step just completed — the answers that segment
  // the funnel (which kitchens, what business, how many hours) without
  // anything personal.
  const stepFacts = (): Record<string, unknown> => {
    switch (step) {
      case 0: return { facility_ids: kitchens, kitchens_count: kitchens.length, kitchens_skipped: kitchens.length === 0 };
      case 2: return { type_id: Number(company.typeId) || null, product_type: company.productType || null };
      case 3: return { total_hours: totalHours, days_count: DAYS.filter((d) => days[d].on).length, kitchen_reqs_count: equipment.length };
      case 4: return { heard: heard || null, has_social: !!(social.instagram || social.twitter || social.linkedIn || social.otherSocials) };
      default: return {};
    }
  };

  const submit = async () => {
    setSending(true);
    setSubmitError('');
    const dayTimes: Record<string, string> = {};
    for (const d of DAYS) if (days[d].on) { dayTimes[`${d}_start`] = days[d].start; dayTimes[`${d}_end`] = days[d].end; }
    // Local midnight of the chosen day, so v2 sees the calendar date the
    // applicant picked rather than the previous evening in Pacific.
    const moveIn = company.moveIn ? parseDay(company.moveIn) : null;
    // The payload v2's intake expects, field for field. api/apply stores it in
    // our own applications table first and forwards it second, so a v2 outage
    // still returns 201 to the applicant.
    const payload = {
      clientKey,
      attribution: applicationAttribution(),
      posthogDeviceId: deviceId(),
      website,
      startedAt: openedAt,
      ...contact,
      company: company.company,
      typeId: Number(company.typeId) || 0,
      productType: company.productType,
      startTime: moveIn && !Number.isNaN(moveIn.getTime()) ? moveIn.toISOString() : '',
      instagram: social.instagram,
      twitter: social.twitter,
      linkedIn: social.linkedIn,
      otherSocials: social.otherSocials,
      kitchenReqs: equipment,
      otherKitchenReqs: otherKitchen,
      otherApplianceReqs: otherAppliance,
      hoursPerWeek: '',
      totalHours,
      workDays: DAYS.filter((d) => days[d].on),
      workTimes: [],
      facilityIds: kitchens,
      howDidYouHear: heard ? [heard.toLowerCase()] : ['other'],
      howDidYouHearOther: '',
      ...dayTimes,
    };
    try {
      const r = await postApplication(payload);
      const data = await r.json().catch(() => ({}));
      if (r.status === 429) {
        // Throttled means an application for this email is ALREADY stored —
        // a success from the applicant's point of view, not an error.
        track('application_submitted', { deduped: true, seconds_to_submit: secs(openedAt) });
        setDoneId(0);
        return;
      }
      if (!r.ok) {
        const e = new Error(data?.error ?? 'submission failed') as Error & { status?: number };
        e.status = r.status;
        throw e;
      }
      track('application_submitted', {
        facility_ids: kitchens, kitchens_count: kitchens.length, total_hours: totalHours,
        type_id: Number(company.typeId) || null,
        days_count: DAYS.filter((d) => days[d].on).length, kitchen_reqs_count: equipment.length,
        heard: heard || null,
        seconds_to_submit: secs(openedAt), back_count: backCount.current, blocked_count: blockedCount.current,
        // What the intake said happened — so a "submitted" in PostHog can be
        // checked against a row that was actually stored and forwarded.
        application_id: data.applicationId ?? null, stored: data.stored ?? null, forwarded: data.forwarded ?? null,
      });
      identifyApplicant(contact.email, { name: `${contact.firstName} ${contact.lastName}`.trim(), company: company.company });
      // Enhanced conversions: first-party match data alongside the conversion.
      const digits = contact.phone.replace(/\D/g, '');
      window.gtag?.('set', 'user_data', { email: contact.email.trim().toLowerCase(), phone_number: digits ? `+1${digits.slice(-10)}` : undefined });
      window.gtag?.('event', 'conversion', { send_to: AW_APPLICATION_CONVERSION });
      window.gtag?.('event', 'application_submitted', { event_category: 'Application' });
      setDoneId(data.applicationId ?? 0);
    } catch (err) {
      const e = err as Error & { status?: number };
      setSubmitError('Something went wrong sending your application. Please try again — or call us at 778.558.7272 and we\u2019ll take it over the phone.');
      track('application_submit_failed', { message: e?.message ?? 'unknown', http_status: e?.status ?? null });
      window.gtag?.('event', 'application_submit_failed', { event_category: 'Application' });
    } finally {
      setSending(false);
    }
  };

  // What is still needed, named, next to the button. role="alert" is assertive
  // and the element is in the DOM on every step (empty when there is nothing
  // wrong), which is what makes the announcement reliable — a live region that
  // only appears with its content is missed by some screen readers.
  const stillNeeded = ERROR_ORDER.filter((k) => errors[k]);
  const actions = (
    <>
      {submitError && <p className="v2form-error v2form-submit-error" role="alert">{submitError}</p>}
    <div className="v2form-alert" role="alert">
      {stillNeeded.length > 0 && `Still needed: ${listNames(stillNeeded as unknown as string[])}.`}
    </div>
    <div className="v2form-actions">
      {step > 0 && <button type="button" className="v2form-btn v2form-back" onClick={back} disabled={sending}>Back</button>}
      <button
        type="button"
        className="v2form-btn v2form-next"
        onClick={step === 5 ? submit : next}
        disabled={sending}
      >
        {step === 5 ? (sending ? 'Sending…' : 'Submit') : 'Next'}
      </button>
    </div>
    </>
  );

  const content: React.ReactNode[] = [
    // 1 — Kitchens
    <>
      <div className="v2form-medium">Which kitchens are you interested in?</div>
      <div className="v2form-tiny">
        View details about each kitchen{' '}
        <a className="v2form-link" href="https://commissaryconnect.com/our-kitchens/">here</a>
      </div>
      <div className="v2form-checklist">
        {KITCHENS.map((k) => (
          <Check key={k.id} label={k.name} hint={addressFor(k.kitchenId)} checked={kitchens.includes(k.id)} onChange={() => toggleId(kitchens, setKitchens, k.id)} />
        ))}
        {OFFSITE.map((o) => (
          <div className="v2form-outlink" key={o.name}>
            <a className="v2form-link" href={o.href}>{o.name}</a>
            <span aria-hidden>ⓘ</span>
          </div>
        ))}
      </div>
      {/* An empty selection is a choice, not an oversight (CMSY-92). */}
      <p className="v2form-tiny v2form-skip">
        Not sure yet?{' '}
        <button type="button" className="v2form-skipbtn" onClick={next}>Skip this step</button>
      </p>
    </>,
    // 2 — Contact Info
    <>
      <div className="v2form-medium">Please enter your contact information.</div>
      <div className="v2form-small">We will only use this information to reach out to you.</div>
      <div className="v2form-row">
        <Field label="First Name" error={errors.firstName}>
          <input autoComplete="given-name" autoCapitalize="words" value={contact.firstName} maxLength={LIMITS.firstName} onChange={(e) => setContact({ ...contact, firstName: e.target.value })} />
        </Field>
        <Field label="Last Name" error={errors.lastName}>
          <input autoComplete="family-name" autoCapitalize="words" value={contact.lastName} maxLength={LIMITS.lastName} onChange={(e) => setContact({ ...contact, lastName: e.target.value })} />
        </Field>
      </div>
      <Field label="Email Address" error={errors.email}>
        <input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false}
          value={contact.email} maxLength={LIMITS.email}
          onChange={(e) => setContact({ ...contact, email: e.target.value })}
          onBlur={saveEarlyPartial}
        />
      </Field>
      <Field label="Phone Number (optional)" error={errors.phone}>
        <input type="tel" inputMode="tel" autoComplete="tel" value={contact.phone} maxLength={LIMITS.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
      </Field>
    </>,
    // 3 — Company Info
    <>
      <div className="v2form-medium is-overfield">Please tell us a little about your company.</div>
      <Field label="Company Name (optional)" error={errors.company}>
        <input autoComplete="organization" value={company.company} maxLength={LIMITS.company} onChange={(e) => setCompany({ ...company, company: e.target.value })} />
      </Field>
      <Field label="Type of Business" error={errors.typeId}>
        <select value={company.typeId} onChange={(e) => setCompany({ ...company, typeId: e.target.value })}>
          <option value="" />
          {BUSINESS_TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Field>
      <Field label="What dishes or menu items do you make?" error={errors.productType}>
        <input value={company.productType} maxLength={LIMITS.productType} onChange={(e) => setCompany({ ...company, productType: e.target.value })} />
      </Field>
      <Field
        outlined
        label="Desired move in date"
        error={errors.moveIn}
        adorn={
          <svg className="v2form-adorn" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 3v4M16 3v4" />
          </svg>
        }
      >
        <input
          type="date"
          min={todayISO}
          max={maxMoveInISO}
          value={company.moveIn}
          onChange={(e) => setCompany({ ...company, moveIn: e.target.value })}
        />
      </Field>
    </>,
    // 4 — Kitchen Info
    <>
      <div className="v2form-medium">Please fill out your kitchen needs.</div>
      <div className="v2form-h18">Kitchen Requirements</div>
      <div className="v2form-checklist">
        {EQUIPMENT.map((k) => (
          <Check key={k} label={k} checked={equipment.includes(k)} onChange={() => toggle(equipment, setEquipment, k)} />
        ))}
      </div>
      <Field label="Other appliance requirements">
        <textarea className="v2form-multi" value={otherAppliance} maxLength={LIMITS.otherApplianceReqs} onChange={(e) => setOtherAppliance(e.target.value)} />
      </Field>
      <div className="v2form-h18 is-afterfield">Which hours do you want to work?</div>
      <div className="v2form-helper">
        Please select a day before selecting a time (Remember to account for prep, service, and clean-up time.)
      </div>
      {errors.days && <p className="v2form-error v2form-days-error" id={DAYS_ERROR_ID}>{errors.days}</p>}
      {DAYS.map((d) => (
        <div className="v2form-dayrow" key={d}>
          <Check
            label={d}
            checked={days[d].on}
            onChange={() => setDays({ ...days, [d]: { ...days[d], on: !days[d].on } })}
            invalid={!!errors.days}
            describedBy={errors.days ? DAYS_ERROR_ID : undefined}
          />
          <div className="v2form-times">
            <select aria-label={`${d} start time`} disabled={!days[d].on} value={days[d].start} onChange={(e) => setDays({ ...days, [d]: { ...days[d], start: e.target.value } })}>
              <option value="">{'\u00a0'}</option>
              {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <span className="v2form-to">to</span>
            <select aria-label={`${d} end time`} disabled={!days[d].on} value={days[d].end} onChange={(e) => setDays({ ...days, [d]: { ...days[d], end: e.target.value } })}>
              <option value="">{'\u00a0'}</option>
              {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      ))}
      <div className="v2form-h18 v2form-total">Total Weekly Hours: {totalHours} hours</div>
      <Field label="Other kitchen requirements">
        <textarea value={otherKitchen} maxLength={LIMITS.otherKitchenReqs} onChange={(e) => setOtherKitchen(e.target.value)} />
      </Field>
    </>,
    // 5 — Social Media
    <>
      <div className="v2form-medium">Please enter your company's social media handles.</div>
      <div className="v2form-small">We would love to connect with your business on social media!</div>
      <Field label="Instagram"><input value={social.instagram} maxLength={LIMITS.instagram} onChange={(e) => setSocial({ ...social, instagram: e.target.value })} /></Field>
      <Field label="Twitter"><input value={social.twitter} maxLength={LIMITS.twitter} onChange={(e) => setSocial({ ...social, twitter: e.target.value })} /></Field>
      <Field label="LinkedIn"><input value={social.linkedIn} maxLength={LIMITS.linkedIn} onChange={(e) => setSocial({ ...social, linkedIn: e.target.value })} /></Field>
      <Field label="Links for any other social media platforms">
        <textarea className="v2form-multi" value={social.otherSocials} maxLength={LIMITS.otherSocials} onChange={(e) => setSocial({ ...social, otherSocials: e.target.value })} />
      </Field>
      <div className="v2form-note">
        For updates on kitchen availability, new products, and the growth of our local food community,
        follow us on Instagram @commissaryconnect. Please like and engage with our content to show
        support for fellow local businesses!
      </div>
      {/* 18px, not the 22px the other step headings use. */}
      <div className="v2form-h18">How did you hear about us?</div>
      {/* v2 renders these as CHECKBOXES in two columns, not a radio list. */}
      <div className="v2form-heard">
        {HEARD.map((h) => (
          <Check key={h} label={h} checked={heard === h} onChange={() => setHeard(heard === h ? '' : h)} />
        ))}
      </div>
    </>,
    // 6 — Final Review
    <>
      <div className="v2form-medium">Review your answers</div>
      {/* Two columns of label-over-value, 16px apart, with the work-hours row
          spanning both. Labels 14px/500 #6b7280, values 18px/700 #4b5563 —
          v2's own type scale for this step, which matches nothing else here. */}
      <div className="v2form-review">
        <Pair label="First Name" value={contact.firstName} />
        <Pair label="Last Name" value={contact.lastName} />
        <Pair label="Email Address" value={contact.email} />
        <Pair label="Phone Number" value={contact.phone} />
        <Pair label="Company Name" value={company.company} />
        <Pair label="Type of Business" value={BUSINESS_TYPES.find((t) => String(t.id) === company.typeId)?.name ?? ''} />
        <Pair label="What dishes or menu items do you make?" value={company.productType} />
        <Pair label="Desired move in date" value={displayDate(company.moveIn)} />
        <Pair label="Other kitchen requirements" value={otherKitchen} />
        <Pair label="Total Weekly Hours" value={totalHours ? `${totalHours.toFixed(2)} hours` : ''} />
        <Pair
          full
          label="Which hours do you want to work?"
          value={DAYS.filter((d) => days[d].on).map((d) => `${d}: ${days[d].start} to ${days[d].end}`).join(', ')}
        />
        <Pair label="How did you hear about us?" value={heard} />
        <Pair label="Other Source" value="" />
      </div>
    </>,
  ];

  return (
    <div
      className="v2form"
      onFocusCapture={markStarted}
      onClickCapture={markStarted}
      onChangeCapture={markStarted}
    >
      {/* INVARIANT (SEO.tsx): every route mounts <SEO />, or it inherits the
          last page's title, canonical and robots tag. noindex on purpose: the
          form is reached from CTAs and ads, not search, and an indexed form
          page would compete with the facility pages that should rank. */}
      <SEO
        title="Apply — Commissary Connect"
        description="Internal test copy of the application form."
        noindex
      />
      <div className="v2form-image">
        <img src="/v2-application-hero.jpg" alt="" />
      </div>
      <div className="v2form-column">
        <div className="v2form-container">
          <img className="v2form-logo" src="/cc-form-logo.svg" alt="Commissary Connect" />
          <div className="v2form-title">Let's Get Cooking!</div>
          {/* Honeypot (COM-982). A filled value quarantines the row server-side:
              stored and reviewable, never forwarded, and the sender still sees
              the 201 they expected. */}
          <input
            className="v2form-hp"
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
          {doneId !== null && (
            <div className="v2form-done" ref={doneRef} tabIndex={-1}>
              <div className="v2form-medium">Application received!</div>
              <div className="v2form-small">
                We have your application and will be in touch shortly. Questions in the
                meantime? Call us at{' '}
                <a className="v2form-link" href="tel:7785587272" data-phone-label="v2_replica_confirmation">778.558.7272</a>.
              </div>
            </div>
          )}
          {doneId === null && STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className={`v2form-step ${i === step ? 'is-active' : i < step ? 'is-done' : ''}`}>
                <div className="v2form-steplabel">
                  <div className="v2form-icon">{i < step ? '\u2713' : i + 1}</div>
                  <span>{label}</span>
                </div>
                {i === step && (
                  <div className="v2form-content" ref={contentRef} tabIndex={-1}>
                    {content[i]}
                    {actions}
                  </div>
                )}
              </div>
              {i < STEPS.length - 1 && <div className="v2form-connector" />}
            </React.Fragment>
          ))}
          <div className="v2form-footer">
            <img src="/powered-by-community-connect.png" alt="Community Connect Logo" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default V2FormReplica;
